(() => {
  "use strict";

  // The viewer's own filter. A person writes, in their own words, what they do
  // not want to see; this file compares those words against the title and body
  // already present in the feed payload and collapses the matching cards.
  //
  // Three properties are the whole point, and each is enforced here rather than
  // promised in a document:
  //
  //   1. The phrases never leave this device. They live in localStorage and are
  //      never sent, never included in a request, never derived into anything
  //      that is sent. tests/personal_filter.test.cjs runs the whole flow with a
  //      fetch that fails the test if it is called at all.
  //   2. The shared order does not change. Nothing is removed or reordered. A
  //      matched Drop stays exactly where it is and collapses into a strip that
  //      says which phrase matched, with a control to open it anyway. PRODUCT.md
  //      reserves precisely this: personal safety filters that remove content
  //      locally without silently producing a different ranked world.
  //   3. A Drop the viewer never saw is not reported as seen. Collapsing removes
  //      both data-integrity-surface, which submitIntegrityImpression reads and
  //      which spends the author's Fair start, and data-analytics-surface, which
  //      the impression observer reads. Opening it puts both back and the view
  //      then counts. The residual inference that leaves is documented at the
  //      call site rather than hidden.
  //
  // Matching is containment, not cosine: how much of the phrase's feature set
  // appears in the Drop. app/semantic.py records the measurement that eliminated
  // cosine. It is lexical, so it matches inflections and Norwegian compounds --
  // "gitar" matches "gitaren" and "gitarforsterker" -- and it does not match
  // synonyms. The interface says so in those words rather than implying that
  // this understands anything.

  const STORAGE_KEY = "louderr.personal-filter.v1";
  const MAX_RULES = 20;
  const MAX_PHRASE_LENGTH = 60;
  const MIN_PHRASE_LENGTH = 2;

  // Named strengths rather than a number, because a cosine-like threshold is not
  // a thing a person can reason about. The values come from the measurement in
  // app/semantic.py: intended matches scored 0.75 and above, unintended ones
  // 0.4333 and below, so "balanced" sits between them and the other two step
  // either side.
  const STRENGTHS = {
    strict: {threshold: 0.85, label: "Only near-identical wording"},
    balanced: {threshold: 0.6, label: "Balanced"},
    broad: {threshold: 0.45, label: "Broad, may hide more than you meant"},
  };
  const DEFAULT_STRENGTH = "balanced";

  // [dataset attribute, where it is parked while the Drop is folded]
  const SEEN_ATTRIBUTES = [
    ["integritySurface", "filteredIntegritySurface"],
    ["analyticsSurface", "filteredAnalyticsSurface"],
  ];

  const revealed = new Set();
  let rules = null;
  let storageWorks = true;

  function similarityApi() {
    return typeof window !== "undefined" ? window.LouderrSimilarity : null;
  }

  function newId() {
    try {
      return crypto.randomUUID();
    } catch (_) {
      return `rule-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    }
  }

  function normalizeRule(candidate) {
    if (!candidate || typeof candidate !== "object") return null;
    const phrase = typeof candidate.phrase === "string" ? candidate.phrase.trim() : "";
    if (phrase.length < MIN_PHRASE_LENGTH || phrase.length > MAX_PHRASE_LENGTH) return null;
    const strength = Object.prototype.hasOwnProperty.call(STRENGTHS, candidate.strength)
      ? candidate.strength
      : DEFAULT_STRENGTH;
    return {id: typeof candidate.id === "string" && candidate.id ? candidate.id : newId(), phrase, strength};
  }

  /** Read the viewer's rules. Never throws: a filter that crashes hides nothing silently. */
  function load() {
    if (rules) return rules;
    rules = [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const list = Array.isArray(parsed) ? parsed : parsed && parsed.rules;
        if (Array.isArray(list)) {
          for (const candidate of list.slice(0, MAX_RULES)) {
            const rule = normalizeRule(candidate);
            if (rule) rules.push(rule);
          }
        }
      }
    } catch (_) {
      // Unreadable or unavailable storage means no filter, which is the safe
      // direction: the viewer sees the shared world, exactly as if they had
      // never set one up.
      storageWorks = false;
      rules = [];
    }
    return rules;
  }

  function save(next) {
    rules = next.slice(0, MAX_RULES);
    try {
      // A clean projection: the cached feature Set that matchText parks on a rule
      // is derived state and has no business in the viewer's stored preference.
      const stored = rules.map(rule => ({id: rule.id, phrase: rule.phrase, strength: rule.strength}));
      localStorage.setItem(STORAGE_KEY, JSON.stringify({version: 1, rules: stored}));
      storageWorks = true;
    } catch (_) {
      storageWorks = false;
    }
    return rules;
  }

  /** The text a rule is matched against: what the author wrote, plus their tags. */
  function dropText(drop) {
    if (!drop) return "";
    const tags = Array.isArray(drop.tags) ? drop.tags.join(" ") : "";
    return [drop.title, drop.body, tags].filter(Boolean).join(" ");
  }

  /**
   * The same text, read back off a rendered card.
   *
   * Used when re-running the filter over what is already on screen, so that no
   * lookup from card back to Drop object is needed and the body text is not
   * duplicated into a data attribute. It is the text the viewer can see, which
   * is the text the filter should be judging.
   */
  function cardText(card) {
    if (!card) return "";
    const parts = [];
    for (const selector of [".drop-title", ".drop-description"]) {
      const node = card.querySelector(selector);
      if (node && node.textContent) parts.push(node.textContent);
    }
    for (const tag of card.querySelectorAll(".drop-tag")) {
      if (tag.textContent) parts.push(tag.textContent.replace(/^#/, ""));
    }
    return parts.join(" ");
  }

  /**
   * The feature set of a rule's phrase, computed once and kept on the rule.
   *
   * Rules are rebuilt by load() and replaced by save(), so a stale cache cannot
   * outlive the phrase it describes. save() writes a clean projection so this
   * never reaches localStorage.
   */
  function phraseFeatures(api, rule) {
    if (!rule.features) rule.features = api.featureSet(rule.phrase);
    return rule.features;
  }

  /**
   * The first rule matching this text, or null.
   *
   * First rather than strongest, so the strip names the phrase the viewer would
   * expect: the one nearest the top of their own list.
   *
   * The Drop's feature set is derived once here rather than once per rule.
   * Measured on a 20-card feed with 20 rules: 18.7 ms the naive way, 1.8 ms with
   * the document set cached, 1.0 ms with the phrase sets cached as well. This
   * runs on every feed render, on whatever device the viewer has, for a feature
   * whose whole justification is that it costs nothing.
   */
  function matchText(text, list = null) {
    const api = similarityApi();
    if (!api || !text) return null;
    const active = list || load();
    if (!active.length) return null;
    const documentFeatures = api.featureSet(text);
    if (!documentFeatures.size) return null;
    for (const rule of active) {
      const strength = STRENGTHS[rule.strength] || STRENGTHS[DEFAULT_STRENGTH];
      const phrase = phraseFeatures(api, rule);
      if (!phrase.size) continue;
      let shared = 0;
      for (const feature of phrase) {
        if (documentFeatures.has(feature)) shared += 1;
      }
      if (shared / phrase.size >= strength.threshold) {
        return {rule, score: shared / phrase.size};
      }
    }
    return null;
  }

  function match(drop, list = null) {
    return matchText(dropText(drop), list);
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  /**
   * Collapse a card that matched, in place.
   *
   * Returns true when the card was collapsed. Called from makeDropCard, so it
   * runs for every surface that builds a Drop card, not only the Floor.
   */
  function applyToCard(card, drop) {
    if (!card) return false;
    const dropId = (drop && drop.id) || card.dataset.dropId;
    if (!dropId || revealed.has(dropId)) return false;
    if (card.dataset.personalFilterHidden === "true") return false;
    const found = matchText(drop ? dropText(drop) : cardText(card));
    if (!found) return false;

    // The two attributes that make a card report itself as seen:
    // data-integrity-surface is what submitIntegrityImpression reads and what
    // spends the author's Fair start, and data-analytics-surface is what the
    // impression observer reads. A folded Drop was not seen, so neither may be
    // reported. Both are kept aside so opening it restores what was there.
    //
    // The cost of this is stated rather than hidden: no impression arrives for
    // these particular Drops, so a determined operator could infer that
    // something was filtered. Absence is already the ordinary case -- scrolling
    // past, closing the tab, a Drop never entering the viewport -- so it is weak
    // evidence, and the alternative is recording that a person saw something
    // they did not. See SEMANTIC-20260910.md.
    for (const [attribute, saved] of SEEN_ATTRIBUTES) {
      const value = card.dataset[attribute];
      if (value === undefined) continue;
      card.dataset[saved] = value;
      delete card.dataset[attribute];
    }
    card.dataset.personalFilterHidden = "true";
    card.classList.add("is-personally-filtered");

    const hidden = [];
    for (const child of Array.from(card.children)) {
      if (child.hidden) continue;
      child.hidden = true;
      hidden.push(child);
    }

    const strip = element("div", "personal-filter-strip");
    const explanation = element("p", "personal-filter-strip-copy");
    explanation.append(
      document.createTextNode("Hidden by your filter: "),
      element("strong", "", found.rule.phrase),
    );
    const open = element("button", "text-button personal-filter-reveal", "Show anyway");
    open.type = "button";
    open.addEventListener("click", () => {
      revealed.add(dropId);
      strip.remove();
      for (const child of hidden) child.hidden = false;
      card.classList.remove("is-personally-filtered");
      delete card.dataset.personalFilterHidden;
      // The viewer chose to look, so this is a real view again: the author's
      // Fair start may count it and the impression observer may report it.
      for (const [attribute, saved] of SEEN_ATTRIBUTES) {
        if (card.dataset[saved] === undefined) continue;
        card.dataset[attribute] = card.dataset[saved];
        delete card.dataset[saved];
      }
      document.dispatchEvent(new CustomEvent("louderr:personal-filter-revealed", {detail: {dropId}}));
    });
    strip.append(explanation, open);
    card.prepend(strip);
    return true;
  }

  /**
   * Fold a Discover grid tile the way applyToCard folds a card: it stays in place and in the
   * count, shows the phrase responsible and offers "Show anyway". While folded the tile is not a
   * link and its label, which carries the post's own text, is set aside.
   */
  function applyToTile(tile, drop) {
    if (!tile || !drop || !drop.id || revealed.has(drop.id)) return false;
    if (tile.dataset.personalFilterHidden === "true") return false;
    const found = matchText(dropText(drop));
    if (!found) return false;
    const href = tile.getAttribute("href");
    const label = tile.getAttribute("aria-label");
    if (href !== null) tile.removeAttribute("href");
    if (label !== null) tile.removeAttribute("aria-label");
    tile.dataset.personalFilterHidden = "true";
    tile.classList.add("is-personally-filtered");
    const hidden = [];
    for (const child of Array.from(tile.children)) {
      if (child.hidden) continue;
      child.hidden = true;
      hidden.push(child);
    }
    const strip = element("div", "personal-filter-strip");
    const explanation = element("p", "personal-filter-strip-copy");
    explanation.append(document.createTextNode("Hidden by your filter: "), element("strong", "", found.rule.phrase));
    const open = element("button", "text-button personal-filter-reveal", "Show anyway");
    open.type = "button";
    open.addEventListener("click", event => {
      event.preventDefault();
      event.stopPropagation();
      revealed.add(drop.id);
      strip.remove();
      for (const child of hidden) child.hidden = false;
      tile.classList.remove("is-personally-filtered");
      delete tile.dataset.personalFilterHidden;
      if (href !== null) tile.setAttribute("href", href);
      if (label !== null) tile.setAttribute("aria-label", label);
      document.dispatchEvent(new CustomEvent("louderr:personal-filter-revealed", {detail: {dropId: drop.id}}));
    });
    strip.append(explanation, open);
    tile.prepend(strip);
    return true;
  }

  /** Rebuild the settings list and re-run the filter over what is already on screen. */
  function announce(status, message) {
    if (status) status.textContent = message;
  }

  function renderSettings(container) {
    const list = container.querySelector("[data-personal-filter-list]");
    const status = container.querySelector("[data-personal-filter-status]");
    if (!list) return;
    list.replaceChildren();
    const active = load();
    if (!active.length) {
      list.append(element("p", "metric-note", "You have not added anything yet."));
    }
    for (const rule of active) {
      const row = element("li", "personal-filter-row");
      const phrase = element("span", "personal-filter-phrase", rule.phrase);
      const select = document.createElement("select");
      select.className = "personal-filter-strength";
      select.setAttribute("aria-label", `How closely a post must match "${rule.phrase}"`);
      for (const [key, value] of Object.entries(STRENGTHS)) {
        const option = document.createElement("option");
        option.value = key;
        option.textContent = value.label;
        if (key === rule.strength) option.selected = true;
        select.append(option);
      }
      select.addEventListener("change", () => {
        save(load().map(item => (item.id === rule.id ? {...item, strength: select.value} : item)));
        announce(status, `Updated "${rule.phrase}".`);
        refresh();
      });
      const remove = element("button", "text-button personal-filter-remove", "Remove");
      remove.type = "button";
      remove.setAttribute("aria-label", `Remove "${rule.phrase}" from your filter`);
      remove.addEventListener("click", () => {
        save(load().filter(item => item.id !== rule.id));
        announce(status, `Removed "${rule.phrase}".`);
        renderSettings(container);
        refresh();
      });
      row.append(phrase, select, remove);
      list.append(row);
    }
    if (!storageWorks) {
      list.append(
        element(
          "p",
          "form-error",
          "This browser is not letting LOUDERR remember anything on this device, so the filter will not survive a reload.",
        ),
      );
    }
  }

  /**
   * Re-apply the filter to every Drop card already rendered.
   *
   * Cards that are already collapsed are left alone rather than rebuilt, and a
   * Drop the viewer chose to open stays open. Removing a phrase does not reopen
   * cards it had collapsed until the next render, which is deliberate: silently
   * expanding content under someone who is reading is worse than a stale strip.
   */
  function refresh() {
    let collapsed = 0;
    for (const card of document.querySelectorAll(".drop-card[data-drop-id]")) {
      if (applyToCard(card, null)) collapsed += 1;
    }
    for (const tile of document.querySelectorAll("#discover-drop-list > .profile-tile[data-drop-id]")) {
      if (applyToTile(tile, tile.filterDrop)) collapsed += 1;
    }
    return collapsed;
  }

  function buildSettings() {
    const anchor = document.getElementById("privacy-preferences");
    if (!anchor || document.getElementById("personal-filter-settings")) return;
    const section = element("section", "appearance-settings");
    section.id = "personal-filter-settings";
    section.setAttribute("aria-labelledby", "personal-filter-title");

    const heading = element("h3", "", "Hide posts with your own words");
    heading.id = "personal-filter-title";
    const note = element(
      "p",
      "metric-note",
      "Write what you would rather not see. LOUDERR compares your words with each post " +
        "in this browser and folds the matching ones away, with a control to open them anyway. " +
        "Nothing here is sent to LOUDERR, the shared order of the Floor does not change, and a " +
        "post you never open does not count towards its author's Fair start.",
    );
    const limits = element(
      "p",
      "metric-note",
      "It matches wording, not meaning. “guitar” also catches “guitars” and " +
        "“guitarist”, but it will not catch “instrument” unless you add that too. " +
        "It is saved on this device only, so it will not follow you to your phone.",
    );

    const form = document.createElement("form");
    form.className = "personal-filter-form";
    const label = element("label", "field");
    label.setAttribute("for", "personal-filter-input");
    label.append(element("span", "", "Word or phrase"));
    const input = document.createElement("input");
    input.id = "personal-filter-input";
    input.type = "text";
    input.maxLength = MAX_PHRASE_LENGTH;
    input.autocomplete = "off";
    input.placeholder = "for example: diet";
    label.append(input);
    const add = element("button", "publish-button", "Add");
    add.type = "submit";
    form.append(label, add);

    const status = element("p", "metric-note");
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    status.dataset.personalFilterStatus = "true";

    const list = document.createElement("ul");
    list.className = "personal-filter-list";
    list.dataset.personalFilterList = "true";

    form.addEventListener("submit", event => {
      event.preventDefault();
      const phrase = input.value.trim();
      if (phrase.length < MIN_PHRASE_LENGTH) {
        announce(status, `Write at least ${MIN_PHRASE_LENGTH} characters.`);
        return;
      }
      const active = load();
      if (active.length >= MAX_RULES) {
        announce(status, `You can keep ${MAX_RULES} at a time. Remove one to add another.`);
        return;
      }
      if (active.some(rule => rule.phrase.toLowerCase() === phrase.toLowerCase())) {
        announce(status, `“${phrase}” is already in your list.`);
        return;
      }
      save(active.concat([{id: newId(), phrase, strength: DEFAULT_STRENGTH}]));
      input.value = "";
      announce(status, `Added “${phrase}”.`);
      renderSettings(section);
      refresh();
    });

    section.append(heading, note, limits, form, status, list);
    anchor.insertAdjacentElement("afterend", section);
    renderSettings(section);
  }

  function start() {
    buildSettings();
    refresh();
  }

  if (typeof document !== "undefined") {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
    else start();
  }

  const api = {
    STORAGE_KEY,
    STRENGTHS,
    DEFAULT_STRENGTH,
    MAX_RULES,
    MAX_PHRASE_LENGTH,
    load,
    save,
    match,
    matchText,
    dropText,
    cardText,
    applyToCard,
    applyToTile,
    refresh,
    buildSettings,
    renderSettings,
    // Tests only: drop the memoized rules so a fresh localStorage is read.
    reset() {
      rules = null;
      revealed.clear();
      storageWorks = true;
    },
  };

  if (typeof window !== "undefined") window.LouderrPersonalFilter = api;
  if (typeof module !== "undefined" && module.exports) module.exports = api;
})();
